import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ValidWidths } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import Card from '@sorare/core/src/components/card/Card';
import { StartingStatus } from '@sorare/core/src/components/draft/StartingStatus';
import { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import RivalsDraftToken from 'components/token/RivalsDraftToken';
import { PrimaryBuyConfirmationOptions } from 'contexts/buyingConfirmation';

import PrimaryBuyBuyField from '../PrimaryBuyBuyField';
import { PrimaryOfferPrice } from '../PrimaryBuyBuyField/Price';
import { Supply } from './Supply';
import { PrimaryBuyPreview_anyCard } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
`;

const Details = styled(Vertical).attrs({ center: true })`
  text-align: center;
  justify-content: stretch;
`;

const Center = styled.div`
  text-align: center;
  display: flex;
  justify-content: center;
`;

const StartingStatusWrapper = styled.div`
  position: absolute;
  top: var(--unit);
  left: var(--unit);
`;

type Props = {
  card: PrimaryBuyPreview_anyCard;
  primaryBuyConfirmationOptions?: PrimaryBuyConfirmationOptions;
  withSupplyCount?: boolean;
  width: ValidWidths;
  origin: ClickInstantBuy_Flow;
  onInfoClick?: (cardSlug: string, playerSlug: string) => void;
  starting?: boolean;
};

export const PrimaryBuyPreview = ({
  card,
  primaryBuyConfirmationOptions,
  withSupplyCount,
  width = 320,
  origin,
  onInfoClick,
  starting,
}: Props) => {
  if (card.tokenOwner || !card.latestPrimaryOffer) return null;

  return (
    <Wrapper>
      <Card card={card} width={width} />
      {starting && (
        <StartingStatusWrapper>
          <StartingStatus isBlockchainCard />
        </StartingStatusWrapper>
      )}
      <Details>
        <RivalsDraftToken card={card} onInfoClick={onInfoClick} />
        {withSupplyCount && (
          <Supply playerDisplayName={card.anyPlayer.displayName || ''} />
        )}
        <Center>
          <PrimaryOfferPrice primaryOffer={card.latestPrimaryOffer} />
        </Center>
      </Details>
      <PrimaryBuyBuyField
        size="small"
        color="tertiary"
        fullWidth
        card={card}
        primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
        origin={origin}
      />
    </Wrapper>
  );
};

PrimaryBuyPreview.fragments = {
  anyCard: gql`
    fragment PrimaryBuyPreview_anyCard on AnyCardInterface {
      slug
      tokenOwner {
        id
      }
      anyPlayer {
        slug
        displayName
      }
      latestPrimaryOffer {
        id
      }
      ...Card_anyCard
      ...PrimaryBuyBuyField_anyCard
      ...RivalsDraftToken_anyCard
    }
    ${Card.fragments.anyCard}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${RivalsDraftToken.fragments.anyCard}
  ` as TypedDocumentNode<PrimaryBuyPreview_anyCard>,
};

export default PrimaryBuyPreview;
